import React from "react";
import { IconWorld, IconArrowRight, IconMapPin } from "@tabler/icons-react";
import SectionCard from "./SectionCard";
import ServiceCard from "./ServiceCard";
// Adjust these two import paths to match where the shared form
// components live in your project.

import type { CountryCode } from "../../store/@types/portal";
import { COUNTRIES, SERVICES_BY_COUNTRY } from "../../constants/countryData";
import SelectInput from "../elements/form/SelectInput";
import Button from "../elements/form/Button";

type CountryServicesStepProps = {
  countryCode: CountryCode | "";
  selectedServiceIds: string[];
  onCountryChange: (code: CountryCode | "") => void;
  onToggleService: (serviceId: string) => void;
  onContinue: () => void;
};

const CountryServicesStep: React.FC<CountryServicesStepProps> = ({
  countryCode,
  selectedServiceIds,
  onCountryChange,
  onToggleService,
  onContinue,
}) => {
  const selectedCountry = COUNTRIES.find(
    (country) => country.code === countryCode,
  );
  const services = countryCode ? SERVICES_BY_COUNTRY[countryCode] : [];

  return (
    <SectionCard
      icon={<IconWorld size={20} stroke={1.75} />}
      title="Select Country & Services"
    >
      <div className="space-y-6">
        <SelectInput
          label="Country of Interest"
          name="country"
          status="warning"
          items={COUNTRIES.map((country) => ({
            id: country.code,
            name: `${country.flagEmoji} ${country.name}`,
          }))}
          value={countryCode}
          onChange={(e) => onCountryChange(e.target.value as CountryCode | "")}
        />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900">
              Services Required
            </span>
            {selectedCountry && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50/10 px-3 py-1 text-sm font-semibold text-primary-600">
                <IconMapPin size={13} stroke={2} />
                {selectedCountry.name}
              </span>
            )}
          </div>

          {countryCode ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  selected={selectedServiceIds.includes(service.id)}
                  onToggle={onToggleService}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
              Choose a country above to see the services available there.
            </div>
          )}
        </div>

        <Button
          title="Continue to Company Details"
          type="button"
          onHandler={onContinue}
          disabled={!countryCode || selectedServiceIds.length === 0}
          rightIcon={<IconArrowRight size={18} />}
          bg_color="bg-primary-500"
          bg_hoverColor="bg-primary-600"
          className="w-full justify-center rounded-full sm:w-auto"
        />
      </div>
    </SectionCard>
  );
};

export default CountryServicesStep;
